import { Link } from 'react-router-dom';
import {
  CalendarDaysIcon,
  DocumentTextIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  VideoCameraIcon,
  ClipboardDocumentCheckIcon,
  BoltIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import LandingLayout from '../components/layout/LandingLayout';

const workflowSteps = [
  {
    num: '01',
    title: 'Client books online',
    desc: 'Clients self-schedule through your branded booking page. Automated reminders reduce no-shows.',
    icon: CalendarDaysIcon,
  },
  {
    num: '02',
    title: 'Run your session',
    desc: 'Launch HIPAA-compliant telehealth or meet in person. Sage handles the rest.',
    icon: VideoCameraIcon,
  },
  {
    num: '03',
    title: 'Document in minutes',
    desc: 'Use templated progress notes, treatment plans, and e-signatures — not blank forms.',
    icon: DocumentTextIcon,
  },
  {
    num: '04',
    title: 'Get paid automatically',
    desc: 'Superbills, insurance claims, and credit card processing — all from the same screen.',
    icon: CreditCardIcon,
  },
];

const features = [
  {
    icon: CalendarDaysIcon,
    title: 'Intelligent scheduling',
    items: [
      'Online booking with custom availability rules',
      'Automated SMS & email reminders',
      'Recurring appointment series',
      'Group therapy & couples scheduling',
    ],
  },
  {
    icon: DocumentTextIcon,
    title: 'Clinical documentation',
    items: [
      'DSM-5 & ICD-10 diagnosis codes',
      'Progress notes, intakes & treatment plans',
      'Structured treatment plans with goals & objectives',
      'Customizable note templates',
    ],
  },
  {
    icon: CreditCardIcon,
    title: 'Billing & insurance',
    items: [
      'Real-time insurance eligibility verification',
      'Automated claim submission (837P)',
      'ERA & EOB reconciliation',
      'Superbills & self-pay invoicing',
    ],
  },
  {
    icon: ShieldCheckIcon,
    title: 'Compliance & security',
    items: [
      'Business Associate Agreement (BAA)',
      'Audit log for all PHI access',
      'AES-256 encryption at rest & in transit',
      'Role-based access control',
    ],
  },
  {
    icon: ChartBarIcon,
    title: 'Practice analytics',
    items: [
      'Revenue & collections dashboard',
      'Appointment utilization reports',
      'Active client & caseload tracking',
      'Insurance payer performance',
    ],
  },
  {
    icon: VideoCameraIcon,
    title: 'Integrated telehealth',
    items: [
      'No downloads required for clients',
      'HIPAA-compliant video sessions',
      'Telehealth link auto-generated per appointment',
      'Works on any device',
    ],
  },
];

const integrations = ['Google Calendar', 'Stripe', 'Availity', 'Change Healthcare', 'Twilio', 'Mailgun'];

const testimonials = [
  {
    quote: "I went from 3 different tools to just Sage. My admin overhead dropped by 60% in the first month.",
    name: 'Dr. Priya Sharma',
    title: 'Psychiatrist, Private Practice · New York, NY',
    avatar: 'PS',
  },
  {
    quote: "The insurance billing module alone is worth the subscription. I'm collecting on claims I used to write off.",
    name: 'David Park, LPC',
    title: 'Group Practice Director · Austin, TX',
    avatar: 'DP',
  },
];

const plans = [
  {
    name: 'Solo',
    price: '$49',
    desc: 'Perfect for individual practitioners.',
    features: ['1 practitioner', 'Unlimited clients', 'Scheduling & notes', 'Basic billing'],
    cta: 'Start free trial',
    highlight: false,
  },
  {
    name: 'Practice',
    price: '$99',
    desc: 'For growing group practices.',
    features: ['Up to 5 practitioners', 'Insurance billing', 'Telehealth included', 'Advanced analytics', 'Priority support'],
    cta: 'Start free trial',
    highlight: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    desc: 'Multi-site organizations & health systems.',
    features: ['Unlimited practitioners', 'Custom integrations', 'Dedicated CSM', 'SLA & compliance docs', 'SSO / SAML'],
    cta: 'Contact sales',
    highlight: false,
  },
];

export default function ForProviders() {
  return (
    <LandingLayout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-950 to-primary-800 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(14,165,233,0.15),_transparent_60%)]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm font-medium text-primary-200 mb-6 border border-white/10">
            <UserGroupIcon className="w-4 h-4" />
            For Mental Health Providers
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight leading-tight mb-5 max-w-3xl">
            Run your practice,{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-300 to-cyan-300">
              not your paperwork
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-white/80 leading-relaxed mb-8 max-w-2xl">
            Sage is the all-in-one practice management platform built for therapists, psychologists, and counselors.
            Less admin, more care.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 bg-white text-primary-700 font-bold px-7 py-3.5 rounded-xl hover:bg-primary-50 transition-colors shadow-lg"
            >
              Start free 30-day trial
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
            <a
              href="#workflow"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3.5 rounded-xl border border-white/20 transition-colors"
            >
              See how it works
            </a>
          </div>
          <p className="mt-4 text-sm text-white/60">Free trial · No credit card · Cancel anytime</p>
        </div>
      </section>

      {/* Workflow steps */}
      <section id="workflow" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-primary-600 uppercase tracking-widest mb-3">How it works</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-neutral-900 tracking-tight">
              Your workflow, simplified
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {workflowSteps.map(({ num, title, desc, icon: Icon }) => (
              <div key={num} className="relative">
                <div className="bg-neutral-50 border border-neutral-100 rounded-2xl p-6 h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl font-extrabold text-neutral-200">{num}</span>
                    <div className="w-9 h-9 bg-primary-50 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary-600" />
                    </div>
                  </div>
                  <h3 className="font-semibold text-neutral-900 mb-2">{title}</h3>
                  <p className="text-sm text-neutral-500 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-primary-600 uppercase tracking-widest mb-3">Features</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-neutral-900 tracking-tight">
              Everything a modern practice needs
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map(({ icon: Icon, title, items }) => (
              <div key={title} className="bg-white rounded-2xl border border-neutral-100 p-6 shadow-card">
                <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-primary-600" />
                </div>
                <h3 className="font-semibold text-neutral-900 mb-3">{title}</h3>
                <ul className="space-y-2">
                  {items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-neutral-600">
                      <CheckCircleIcon className="w-4 h-4 text-success-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance callout */}
      <section className="py-16 bg-primary-950 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-10">
            <div className="flex-1">
              <div className="w-12 h-12 bg-primary-800 rounded-xl flex items-center justify-center mb-5">
                <ShieldCheckIcon className="w-7 h-7 text-primary-300" />
              </div>
              <h2 className="text-3xl font-extrabold mb-4">HIPAA compliance, without the headache</h2>
              <p className="text-white/75 leading-relaxed mb-6 max-w-lg">
                Sage was built HIPAA-first. Every feature — from messaging to billing — is designed to protect
                patient health information. We sign a Business Associate Agreement with every customer.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  'BAA included for all plans',
                  'AES-256 encryption',
                  'Audit logs for all PHI access',
                  'Role-based permissions',
                  'SOC 2 Type II (in progress)',
                  'Annual security reviews',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-white/80">
                    <CheckCircleIcon className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0 bg-white/5 border border-white/10 rounded-2xl p-6 w-full lg:w-72">
              <div className="flex items-center gap-2 mb-4">
                <ClipboardDocumentCheckIcon className="w-5 h-5 text-primary-400" />
                <p className="text-sm font-semibold text-white">Compliance checklist</p>
              </div>
              {[
                { label: 'Business Associate Agreement', done: true },
                { label: 'Encryption at rest', done: true },
                { label: 'Encryption in transit (TLS)', done: true },
                { label: 'Audit logging', done: true },
                { label: 'Access controls', done: true },
                { label: 'Breach notification policy', done: true },
              ].map(({ label, done }) => (
                <div key={label} className="flex items-center gap-2 py-2 border-b border-white/5 last:border-0">
                  <div className={`w-4 h-4 rounded-full flex-shrink-0 ${done ? 'bg-success-500' : 'bg-neutral-700'}`} />
                  <span className="text-sm text-primary-200">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-neutral-900 tracking-tight">Trusted by clinicians</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {testimonials.map(({ quote, name, title, avatar }) => (
              <div key={name} className="bg-neutral-50 border border-neutral-100 rounded-2xl p-7">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-warning-400 fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-neutral-700 leading-relaxed mb-5">"{quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-bold">
                    {avatar}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-neutral-900">{name}</p>
                    <p className="text-xs text-neutral-500">{title}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-12 bg-neutral-50 border-t border-neutral-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-semibold text-neutral-400 uppercase tracking-widest mb-6">Integrates with the tools you already use</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {integrations.map((name) => (
              <span key={name} className="bg-white border border-neutral-200 text-neutral-600 text-sm font-medium px-4 py-2 rounded-lg shadow-xs">
                {name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-primary-600 uppercase tracking-widest mb-3">Pricing</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-neutral-900 tracking-tight">Simple, transparent pricing</h2>
            <p className="mt-3 text-neutral-500">All plans include a free 30-day trial. No credit card required.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map(({ name, price, desc, features: planFeatures, cta, highlight }) => (
              <div
                key={name}
                className={`rounded-2xl p-7 flex flex-col ${
                  highlight
                    ? 'bg-primary-600 text-white ring-2 ring-primary-600 shadow-xl'
                    : 'bg-white border border-neutral-100 shadow-card'
                }`}
              >
                {highlight && (
                  <span className="inline-block bg-white/20 text-white text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full mb-3 w-fit">
                    Most popular
                  </span>
                )}
                <p className={`text-lg font-bold mb-0.5 ${highlight ? 'text-white' : 'text-neutral-900'}`}>{name}</p>
                <p className={`text-sm mb-4 ${highlight ? 'text-primary-200' : 'text-neutral-500'}`}>{desc}</p>
                <div className="mb-5">
                  <span className={`text-4xl font-extrabold ${highlight ? 'text-white' : 'text-neutral-900'}`}>{price}</span>
                  {price !== 'Custom' && <span className={`text-sm ${highlight ? 'text-primary-200' : 'text-neutral-500'}`}>/mo</span>}
                </div>
                <ul className="space-y-2.5 flex-1 mb-6">
                  {planFeatures.map((f) => (
                    <li key={f} className={`flex items-start gap-2 text-sm ${highlight ? 'text-primary-100' : 'text-neutral-600'}`}>
                      <CheckCircleIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${highlight ? 'text-primary-200' : 'text-success-500'}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className={`block text-center py-3 rounded-xl text-sm font-semibold transition-colors ${
                    highlight
                      ? 'bg-white text-primary-700 hover:bg-primary-50'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  }`}
                >
                  {cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-primary-700 to-primary-900 text-white text-center">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <BoltIcon className="w-10 h-10 text-primary-300 mx-auto mb-4" />
          <h2 className="text-3xl font-extrabold mb-3">Get started in under 10 minutes</h2>
          <p className="text-white/80 mb-8">Set up your practice, import clients, and start scheduling — today.</p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-white text-primary-700 font-bold px-7 py-3.5 rounded-xl hover:bg-primary-50 transition-colors shadow-lg"
          >
            Start free trial <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </LandingLayout>
  );
}
